/* eslint-disable max-lines */
import { ElementRef, Inject, Input, ViewChild, Component } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Dictionary } from '@ngrx/entity';
import { Observable, zip, Subscription, of } from 'rxjs';
import { distinctUntilChanged, map, share, tap } from 'rxjs/operators';
import { isNullOrUndefined } from '@app/util/util';
import { TranslocoService } from '@jsverse/transloco';

import { AppFacade } from '@app/app.facade';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { BusCompany } from '@app/entities/bus-company';
import { BusResultsFacade } from '@buses/bus-results/bus-results.facade';
import { AirSearchPayload } from '@flight-results/entities/airSearchPayload';
import { AirportsFilter } from '@flight-results/entities/request-search.payload';
import { FlightResultsFacade } from '@flight-results/flight-results.facade';
import * as filterMappers from '@flight-results/mappers/filters.mapper';
import {
  CheckboxFilter, PickedRange, RangeResponseFilter
} from '@shared/filters/entities/fetch-filters.response';
import { getPickedRangeLength } from '@shared/filters/mappers/range-filter.mapper';
import { FilterTypes, SectorTypes, StopTypes, AirportFilter } from '@shared/flight-itinerary/entities/filters';
import { SorterTypes } from '@shared/filters/entities/sorter-options';
import { ExchangeSearchPayload } from '@user-history/entities/user-history-item';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { TriggerAmplitudeService } from '@amplitude/services/trigger-amplitude.service';
import { TripTypes } from '@app/entities/trip-types';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { mapExchangePayload, PayloadExchange } from '@flight-results/mappers/exchange-payload.mapper';
import { mapPayload, Payload } from '@flight-results/mappers/search-payload.mapper';
import { mapPayload as mapPayloadBus, Payload as PayloadBus } from '@buses/bus-results/mappers/search-payload.mapper';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { FlightsBaseFiltersAbstract } from '@shared/filters/abstract/flights-base-filters.abstract';
import { HourWidget, PriceWidget } from '@app/entities/flight-results-widget';
import { StatsigExperimentsFacade } from '@statsig/statsig.facade';
import { BusSearchPayload } from '@buses/bus-results/entities/bus-search-payload';
import { RangeFilterPayload } from '@shared/filters/entities/range-filter';
import { AirFilterResponseVm, AirFilterVm } from '@shared/magic-filter-bar/entities/filter-types';
import {
  isCheckBoxByAirFilter, isCheckRangeByAirFilter, isSectionCheckBoxByAirFilter
} from '@app/mappers/global-filters/air-global-filters.mapper';

@Component({
  template: '',
  standalone: false
})
export class FlightsModalFiltersAbstractComponent extends FlightsBaseFiltersAbstract {

  @Input() filterBuses: boolean;
  @Input() isExchangeDesktop: boolean;

  @ViewChild('deleteButton') public deleteButton: ElementRef;

  protected subscriptions: Subscription[] = [];
  protected params: AirSearchPayload | ExchangeSearchPayload | BusSearchPayload = {} as AirSearchPayload;
  protected hasChangeSortByFilter: boolean = false;

  public pickedCheckboxes: string[] = [];
  public pickedAirportsCheckboxes: AirportsFilter = {} as AirportsFilter;
  public pickedRanges: PickedRange = {};

  constructor(
    protected appFacade: AppFacade,
    protected busFacade: BusResultsFacade,
    protected fligthfacade: FlightResultsFacade,
    protected baseSearchBarFacade: BaseSearchBarFacade,
    protected route: ActivatedRoute,
    protected amplitude: PropertiesAmplitudeService,
    protected triggerAmplitudeService: TriggerAmplitudeService,
    protected translateService: TranslocoService,
    protected domManipulateService: DomManipulateService,
    protected statsigExperimentsFacade: StatsigExperimentsFacade,
    @Inject(IS_BROWSER) protected isBrowser: boolean
  ) {
    super(fligthfacade, busFacade, amplitude, triggerAmplitudeService, domManipulateService);
  }

  get params$(): Observable<Params> {
    return this.route.parent.params;
  }

  get filters$(): Observable<AirFilterResponseVm> {
    return this.isBuses ? this.busFacade.filters$ : this.fligthfacade.filters$;
  }

  get isExchangeFilter(): boolean {
    return this.isExchangeDesktop || this.isChanges;
  }

  get maxPriceAlert$(): Observable<number> {
    return this.fligthfacade.filters$.pipe(map(filters => {
      const airFilterTabs = filters.AirFilterTabs;
      const priceTab = airFilterTabs ? airFilterTabs.find(tab => tab.OrderCriteria === SorterTypes.Price) : null;
      return priceTab && priceTab.Amount ? +priceTab.Amount : null;
    }));
  }

  get pickedCheckboxes$(): Observable<string[]> {
    if (this.isBuses) {
      return this.busFacade.searchPayload$.pipe(
        map(payload => filterMappers.pickedCheckboxes(payload)), share()
      );
    }
    return this.fligthfacade.searchPayload$.pipe(
      map(payload => filterMappers.pickedCheckboxes(payload)), share()
    );
  }

  get pickedAirportsCheckboxes$(): Observable<AirportsFilter> {
    if (this.isBuses) {
      return this.busFacade.searchPayload$.pipe(
        map(payload => filterMappers.pickedAirportCheckboxes(payload)), share()
      );
    }
    return this.fligthfacade.searchPayload$.pipe(
      map(payload => filterMappers.pickedAirportCheckboxes(payload)), share()
    );
  }

  get pickedRanges$(): Observable<PickedRange> {
    if (this.isBuses) {
      return this.busFacade.searchPayload$.pipe(
        map(payload => filterMappers.pickedRanges(payload)), share()
      );
    }
    return this.fligthfacade.searchPayload$.pipe(
      map(payload => filterMappers.pickedRanges(payload)), share()
    );
  }

  get orderCriteria$(): Observable<SorterTypes> {
    if (this.isBuses) return this.busFacade.orderCriteria$;
    return this.fligthfacade.orderCriteria$;
  }

  get orderCriteriaSectorNumber$(): Observable<number> {
    return this.fligthfacade.orderCriteriaSectorNumber$;
  }

  get busCompaniesEntities$(): Observable<Dictionary<BusCompany>> {
    return this.appFacade.busCompaniesEntities$;
  }

  get infoPriceWidgets$(): Observable<PriceWidget[]> {
    return this.fligthfacade.infoPriceWidgets$;
  }

  get infoDepartureWidget$(): Observable<HourWidget[]> {
    return this.fligthfacade.infoDepartureWidget$;
  }

  get infoDepartureWidgetReturn$(): Observable<HourWidget[]> {
    return this.fligthfacade.infoDepartureWidgetReturn$;
  }

  get infoFlightDurationWidget$(): Observable<HourWidget[]> {
    return this.fligthfacade.infoFlightDurationWidget$;
  }

  get infoFlightDurationWidgetReturn$(): Observable<HourWidget[]> {
    return this.fligthfacade.infoFlightDurationWidgetReturn$;
  }

  get someFilterSelected(): boolean {
    return !isNullOrUndefined(this.filtersSelectedCount) || this.hasChangeSortByFilter;
  }

  get totalFilterCount(): number {
    const NumberOfCheckboxes = this.pickedCheckboxes ? this.pickedCheckboxes?.length : 0;
    const numberOfRanges = this.pickedRanges ? this.getRangeLength(this.pickedRanges) : 0;
    const NumberOfDepartureAirports = this.pickedAirportsCheckboxes?.DepartureAirports
      && this.pickedAirportsCheckboxes?.DepartureAirports?.length > 0 ?
      this.pickedAirportsCheckboxes?.DepartureAirports
        .map(x => x?.Airports?.length).reduce((sum, value) => sum + value, 0) : 0;
    const NumberOfArrivalAirports = this.pickedAirportsCheckboxes?.ArrivalAirports &&
      this.pickedAirportsCheckboxes?.ArrivalAirports.length > 0 ?
      this.pickedAirportsCheckboxes.ArrivalAirports
        .map(x => x.Airports.length).reduce((sum, value) => sum + value, 0) : 0;
    return NumberOfCheckboxes + NumberOfDepartureAirports + NumberOfArrivalAirports + numberOfRanges;
  }

  get filtersSelectedCount(): string {
    return this.totalFilterCount > 0 ? `(${this.totalFilterCount})` : null;
  }

  get departureType(): string {
    return SectorTypes.Sector1;
  }

  get returnType(): string {
    return SectorTypes.Sector2;
  }

  get directOnly$(): Observable<boolean> {
    return this.fligthfacade.searchPayload$.pipe(map(searchPayload => {
      return !!searchPayload && !!searchPayload.Stops && !!searchPayload.Stops.find(stop => stop === StopTypes.Stops0);
    }));
  }

  get isAnInternationalTrip$(): Observable<boolean> {
    return this.fligthfacade.isAnInternationalTrip$;
  }

  public rangeFilter$(type: FilterTypes, sector?: TripTypes): Observable<PriceWidget[] | HourWidget[]> {
    switch (type) {
      case FilterTypes.PriceRange:
        return this.infoPriceWidgets$;
      case FilterTypes.DepartureTimes:
        return sector === TripTypes.DEPARTURE ? this.infoDepartureWidget$ : this.infoDepartureWidgetReturn$;
      case FilterTypes.SectorDurations:
        return sector === TripTypes.DEPARTURE ? this.infoFlightDurationWidget$ : this.infoFlightDurationWidgetReturn$;
      default:
        return of(null);
    }
  }

  public defineBodyScroll(state: boolean): void {
    if (this.isMobile) {
      this.domManipulateService.setBodyScroll(state);
    }
  }

  public getRangeLength(ranges: PickedRange): number {
    return getPickedRangeLength(ranges);
  }

  public isCheckboxFilter(item: AirFilterVm): boolean {
    return isCheckBoxByAirFilter(item.type) && (item.value as CheckboxFilter[]).length > 0;
  }

  public hasFilterAirline(item: AirFilterVm): boolean {
    return item.type !== FilterTypes.Airlines ||
      (item.type === FilterTypes.Airlines && (item.value as AirportFilter[]).length > 1);
  }

  public isSectionCheckBoxFilter(item: AirFilterVm): boolean {
    return isSectionCheckBoxByAirFilter(item.type);
  }

  public isRangeFilter(item: AirFilterVm): boolean {
    return isCheckRangeByAirFilter(item.type);
  }

  public isSorter(item: AirFilterVm): boolean {
    return item.type === FilterTypes.AirFilterTabs && this.isMobile;
  }

  public valuesIsArray(item: AirFilterVm): boolean {
    return Array.isArray(item.value);
  }

  public applyRangeFilter(type: FilterTypes, item: RangeResponseFilter, event: RangeFilterPayload): void {
    const { range, removable } = event;
    if (type === FilterTypes.SectorDurations || type === FilterTypes.DepartureTimes) {
      this.searchFlightsWithFilter({ type, value: range, removable, sector: item.SectorNumber });
    } else {
      this.searchFlightsWithFilter({ type, value: range, removable });
    }
  }

  public getCriteria(type: FilterTypes, text?: string): string {
    const params = this.isMobile ? this.route.parent.snapshot.params : this.route.snapshot.params;
    const selectedIds = params?.selected_ids?.split('-') || [];
    const departure = selectedIds.length > 0 ? params.arrival : params.departure;
    const arrival = selectedIds.length > 0 ? params.departure : params.arrival;
    if (isNullOrUndefined(text)) return '';

    switch (type) {
      case FilterTypes.SectorDurations:
      case FilterTypes.DepartureTimes:
        return this.durationsCriteria(text, departure, arrival);
      case FilterTypes.DepartureAirports:
      case FilterTypes.ArrivalAirports:
        return this.airportsCriteria(text, departure, arrival);
      case FilterTypes.AirFilterTabs:
        return text === this.returnType ? arrival : departure;
      default:
        return '';
    }
  }

  private durationsCriteria(text: string, departure: string, arrival: string): string {
    const tripType: TripTypes = parseInt(text);
    if (this.isBuses) {
      return `- ${this.translateService.translate(
        tripType === TripTypes.DEPARTURE ? 'BUS_RESULTS_MODULE.ITINERARY_LIST_SECTION.DEPARTURE_TRIP' :
          'BUS_RESULTS_MODULE.ITINERARY_LIST_SECTION.RETURN_TRIP')}`;
    } else {
      return tripType === TripTypes.DEPARTURE ? `${departure} - ${arrival}` : `${arrival} - ${departure}`;
    }
  }

  private airportsCriteria(text: string, departure: string, arrival: string): string {
    if (this.isBuses) {
      return ` - ${this.translateService.translate(
        text === this.returnType ? 'BUS_RESULTS_MODULE.ITINERARY_LIST_SECTION.RETURN_TRIP' :
          'BUS_RESULTS_MODULE.ITINERARY_LIST_SECTION.DEPARTURE_TRIP')}`;
    } else {
      return text === this.returnType ? `${arrival} - ${departure}` : `${departure} - ${arrival}`;
    }
  }

  public listenParams(): void {
    if (this.isBrowser) {
      this.subscriptions.push(this.pickedCheckboxes$.subscribe(checkboxes => {
        this.pickedCheckboxes = checkboxes;
      }));
      this.subscriptions.push(this.pickedAirportsCheckboxes$.subscribe(airports => {
        this.pickedAirportsCheckboxes = airports;
      }));
      this.subscriptions.push(this.pickedRanges$.subscribe(ranges => {
        this.pickedRanges = ranges;
      }));
      if (!this.isBuses) {
        this.subscriptions.push(
          this.fligthfacade.sortingHasBeenChanged$.pipe(
            distinctUntilChanged(),
            tap((sortHasBeenChanged) => {
              this.hasChangeSortByFilter = sortHasBeenChanged;
            })
          ).subscribe()
        );
      }
      if (this.isMobile) {
        zip(this.params$, this.baseSearchBarFacade.branchCode$, this.baseSearchBarFacade.salesChannel$)
          .subscribe(([parentParams, branchCode, salesChannel]) => {
            this.params = this.isBuses ? mapPayloadBus(parentParams as PayloadBus, true) : this.isExchangeFilter ?
              mapExchangePayload(parentParams as PayloadExchange, true) : mapPayload(parentParams as Payload, true, false);
            this.params.RequestSettings.BranchCode = branchCode; this.params.RequestSettings.SalesChanelCode = salesChannel;
          }).unsubscribe();
      }
    }
  }
}
